import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import SearchIcon from "@mui/icons-material/Search";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  FINANCEIRO_ACCENT,
  financeiroToolbarSx,
} from "../../components/financeiro-lancamentos.theme";
import type { CentroCustoApi } from "../types/centro-custo.types";
import type {
  ContasPagarFiltros as ContasPagarFiltrosState,
  FornecedorFinanceiroApi,
} from "../types/contas-pagar.types";
import {
  periodoVencimentoParaDatasCp,
  type ContasPagarPeriodoVencimentoPreset,
} from "../helpers/contas-pagar-datas";

type OpcaoStr = { value: string; label: string };

const OPCOES_STATUS: OpcaoStr[] = [
  { value: "", label: "Todos" },
  { value: "PENDENTE", label: "Pendente" },
  { value: "PAGO", label: "Pago" },
  { value: "VENCIDO", label: "Vencido" },
  { value: "CANCELADO", label: "Cancelado" },
];

const OPCOES_CATEGORIA: OpcaoStr[] = [
  { value: "", label: "Todas" },
  { value: "MARKETING", label: "Marketing" },
  { value: "FRETE", label: "Frete" },
  { value: "INFRAESTRUTURA", label: "Infraestrutura" },
  { value: "SERVICOS", label: "Serviços" },
  { value: "EVENTOS", label: "Eventos" },
  { value: "FORNECEDORES", label: "Fornecedores" },
  { value: "IMPOSTOS", label: "Impostos" },
  { value: "OPERACIONAL", label: "Operacional" },
  { value: "OUTROS", label: "Outros" },
];

const OPCOES_PERIODO: { value: ContasPagarPeriodoVencimentoPreset; label: string }[] = [
  { value: "hoje", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "mes", label: "Este mês" },
];

const toggleSx = {
  flexWrap: "wrap" as const,
  "& .MuiToggleButton-root": {
    px: 1.25,
    py: 0.75,
    textTransform: "none" as const,
    fontSize: "0.8125rem",
    "&.Mui-selected": {
      bgcolor: "rgba(249, 96, 48, 0.1)",
      borderColor: FINANCEIRO_ACCENT,
      color: FINANCEIRO_ACCENT,
      fontWeight: 600,
    },
  },
};

const fieldSx = { bgcolor: "background.paper", borderRadius: 1.5 };

export type ContasPagarFiltrosProps = {
  filtros: ContasPagarFiltrosState;
  setFiltros: (
    patch:
      | Partial<ContasPagarFiltrosState>
      | ((p: ContasPagarFiltrosState) => ContasPagarFiltrosState),
  ) => void;
  onAplicar: () => void;
  onLimpar: () => void;
  loading?: boolean;
  centros: CentroCustoApi[];
  centrosLoading?: boolean;
  fornecedores: FornecedorFinanceiroApi[];
  fornecedoresLoading?: boolean;
  resetPeriodoPresetTick?: number;
};

export default function ContasPagarFiltros({
  filtros,
  setFiltros,
  onAplicar,
  onLimpar,
  loading,
  centros,
  centrosLoading,
  fornecedores,
  fornecedoresLoading,
  resetPeriodoPresetTick = 0,
}: ContasPagarFiltrosProps) {
  const [periodoPreset, setPeriodoPreset] =
    useState<ContasPagarPeriodoVencimentoPreset | "personalizado">("mes");

  useEffect(() => {
    setPeriodoPreset("mes");
  }, [resetPeriodoPresetTick]);

  const aplicarPeriodo = (preset: ContasPagarPeriodoVencimentoPreset) => {
    setPeriodoPreset(preset);
    const datas = periodoVencimentoParaDatasCp(preset);
    if (datas) {
      setFiltros((prev) => ({
        ...prev,
        vencimentoInicio: datas.vencimentoInicio,
        vencimentoFim: datas.vencimentoFim,
      }));
    }
  };

  const patch =
    (field: keyof ContasPagarFiltrosState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFiltros((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const opcoesCentro: OpcaoStr[] = [
    { value: "", label: "Todos" },
    ...centros.map((c) => ({ value: c.id, label: c.nome })),
  ];

  const opcoesFornecedor: OpcaoStr[] = [
    { value: "", label: "Todos" },
    ...fornecedores.map((f) => ({
      value: f.id,
      label: f.documento ? `${f.nome} (${f.documento})` : f.nome,
    })),
  ];

  const centroSelecionado =
    opcoesCentro.find((o) => o.value === (filtros.centroCustoId ?? "")) ?? opcoesCentro[0];
  const fornecedorSelecionado =
    opcoesFornecedor.find((o) => o.value === (filtros.fornecedorId ?? "")) ??
    opcoesFornecedor[0];

  const periodoToggleValue =
    periodoPreset === "personalizado" ? "" : periodoPreset;

  return (
    <MDBox sx={financeiroToolbarSx}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterListOutlinedIcon sx={{ color: FINANCEIRO_ACCENT }} />
          <MDTypography variant="button" fontWeight="medium" color="text">
            Filtros operacionais
          </MDTypography>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Status
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filtros.status ?? ""}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              setFiltros((prev) => ({ ...prev, status: next }));
            }}
            sx={toggleSx}
          >
            {OPCOES_STATUS.map((opt) => (
              <ToggleButton
                key={opt.value || "all-status"}
                value={opt.value}
                disabled={Boolean(loading)}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Vencimento
          </Typography>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "flex-end" }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={periodoToggleValue}
              onChange={(_, next: ContasPagarPeriodoVencimentoPreset | null) => {
                if (next) aplicarPeriodo(next);
              }}
              sx={{ ...toggleSx, flexShrink: 0 }}
            >
              {OPCOES_PERIODO.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value} disabled={Boolean(loading)}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel shrink htmlFor="cp-venc-inicio">
                Vencimento início
              </InputLabel>
              <OutlinedInput
                id="cp-venc-inicio"
                type="date"
                value={filtros.vencimentoInicio ?? ""}
                onChange={(e) => {
                  setPeriodoPreset("personalizado");
                  patch("vencimentoInicio")(e);
                }}
                label="Vencimento início"
                notched
                disabled={Boolean(loading)}
                sx={fieldSx}
              />
            </FormControl>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel shrink htmlFor="cp-venc-fim">
                Vencimento fim
              </InputLabel>
              <OutlinedInput
                id="cp-venc-fim"
                type="date"
                value={filtros.vencimentoFim ?? ""}
                onChange={(e) => {
                  setPeriodoPreset("personalizado");
                  patch("vencimentoFim")(e);
                }}
                label="Vencimento fim"
                notched
                disabled={Boolean(loading)}
                sx={fieldSx}
              />
            </FormControl>
          </Stack>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Categoria
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filtros.categoria ?? ""}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              setFiltros((prev) => ({ ...prev, categoria: next }));
            }}
            sx={toggleSx}
          >
            {OPCOES_CATEGORIA.map((opt) => (
              <ToggleButton
                key={opt.value || "all-categoria"}
                value={opt.value}
                disabled={Boolean(loading)}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "flex-end" }}
        >
          <Autocomplete<OpcaoStr, false, false, false>
            size="small"
            options={opcoesCentro}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            value={centroSelecionado}
            onChange={(_, v) =>
              setFiltros((prev) => ({ ...prev, centroCustoId: v?.value ?? "" }))
            }
            disabled={Boolean(loading || centrosLoading)}
            loading={Boolean(centrosLoading)}
            sx={{ flex: 1, minWidth: 0 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Centro de custo"
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                sx={fieldSx}
              />
            )}
          />

          <Autocomplete<OpcaoStr, false, false, false>
            size="small"
            options={opcoesFornecedor}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            value={fornecedorSelecionado}
            onChange={(_, v) =>
              setFiltros((prev) => ({ ...prev, fornecedorId: v?.value ?? "" }))
            }
            disabled={Boolean(loading || fornecedoresLoading)}
            loading={Boolean(fornecedoresLoading)}
            sx={{ flex: 1, minWidth: 0 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Fornecedor"
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                sx={fieldSx}
              />
            )}
          />
        </Stack>

        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel shrink htmlFor="cp-busca">
            Busca
          </InputLabel>
          <OutlinedInput
            id="cp-busca"
            value={filtros.busca ?? ""}
            onChange={patch("busca")}
            label="Busca"
            placeholder="Fornecedor, descrição, documento…"
            notched
            disabled={Boolean(loading)}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            }
            sx={fieldSx}
          />
        </FormControl>

        <Divider />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(filtros.apenasVencidos)}
                onChange={(_, checked) =>
                  setFiltros((prev) => ({ ...prev, apenasVencidos: checked }))
                }
                disabled={Boolean(loading)}
              />
            }
            label={<Typography variant="body2">Apenas vencidos</Typography>}
            sx={{ m: 0 }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              disabled={Boolean(loading)}
              onClick={onAplicar}
              sx={{
                background: `linear-gradient(195deg, ${FINANCEIRO_ACCENT}, #e04a1a)`,
              }}
            >
              Aplicar filtros
            </MDButton>
            <MDButton
              color="dark"
              variant="outlined"
              size="small"
              disabled={Boolean(loading)}
              onClick={onLimpar}
            >
              Limpar
            </MDButton>
          </Stack>
        </Stack>
      </Stack>
    </MDBox>
  );
}
